// Canal imperativo entre páginas e o widget flutuante (montado no layout
// persistente). Duas direções: a página do processo manda o widget abrir a
// conversa de edição; o widget avisa as páginas quando uma revisão do intake
// foi aplicada, para a página recarregar o workflow. Módulo-nível de
// propósito: sem biblioteca de estado, os handlers só existem enquanto os
// componentes estão montados.

import type { ProcedurePresentation } from '@/frontend/features/AgentChats/events/agentEvents'

export interface OpenBusinessProcessEditChatCommand {
  processId: string
  workerId: string
}

export interface BusinessProcessRevisedNotice {
  processId: string
  // ISO8601 da aplicação da revisão (revised_business_process_at); muda a cada
  // revisão aplicada, então serve de gatilho para recargas repetidas.
  at: string | null
}

export interface BusinessProcessEditChatVisibility {
  processId: string
  open: boolean
}

export interface BusinessProcessDraftPreviewNotice {
  processId: string
  procedure: ProcedurePresentation | null
}

type OpenHandler = (command: OpenBusinessProcessEditChatCommand) => void
type RevisedHandler = (notice: BusinessProcessRevisedNotice) => void
type VisibilityHandler = (notice: BusinessProcessEditChatVisibility) => void
type DraftPreviewHandler = (notice: BusinessProcessDraftPreviewNotice) => void

const openHandlers = new Set<OpenHandler>()
const revisedHandlers = new Set<RevisedHandler>()
const visibilityHandlers = new Set<VisibilityHandler>()
const draftPreviewHandlers = new Set<DraftPreviewHandler>()
let latestVisibilityNotice: BusinessProcessEditChatVisibility | null = null
let latestDraftPreviewNotice: BusinessProcessDraftPreviewNotice | null = null
// O widget é lazy-loaded: um clique em Editar pode disparar antes de o chunk
// do widget assinar o canal. O último comando fica guardado e é entregue na
// assinatura, para o clique nunca se perder na janela de carregamento.
let pendingOpenCommand: OpenBusinessProcessEditChatCommand | null = null

export function openBusinessProcessEditChat(command: OpenBusinessProcessEditChatCommand) {
  if (openHandlers.size === 0) {
    pendingOpenCommand = command
    return
  }
  openHandlers.forEach((handler) => handler(command))
}

export function subscribeBusinessProcessEditChat(handler: OpenHandler): () => void {
  openHandlers.add(handler)
  if (pendingOpenCommand) {
    const command = pendingOpenCommand
    pendingOpenCommand = null
    handler(command)
  }
  return () => openHandlers.delete(handler)
}

export function notifyBusinessProcessRevised(notice: BusinessProcessRevisedNotice) {
  revisedHandlers.forEach((handler) => handler(notice))
}

export function subscribeBusinessProcessRevised(handler: RevisedHandler): () => void {
  revisedHandlers.add(handler)
  return () => revisedHandlers.delete(handler)
}

export function notifyBusinessProcessEditChatVisibility(notice: BusinessProcessEditChatVisibility) {
  latestVisibilityNotice = notice
  visibilityHandlers.forEach((handler) => handler(notice))
}

export function subscribeBusinessProcessEditChatVisibility(handler: VisibilityHandler): () => void {
  visibilityHandlers.add(handler)
  if (latestVisibilityNotice) handler(latestVisibilityNotice)
  return () => visibilityHandlers.delete(handler)
}

export function notifyBusinessProcessDraftPreview(notice: BusinessProcessDraftPreviewNotice) {
  latestDraftPreviewNotice = notice
  draftPreviewHandlers.forEach((handler) => handler(notice))
}

export function subscribeBusinessProcessDraftPreview(handler: DraftPreviewHandler): () => void {
  draftPreviewHandlers.add(handler)
  if (latestDraftPreviewNotice) handler(latestDraftPreviewNotice)
  return () => draftPreviewHandlers.delete(handler)
}
